import _ from 'lodash'
import { buildWidgetUrl } from './resolveRemoteRefs'
import {
	pointers,
	Component,
	Structure,
	Data,
	DataMapName,
	QueryProperty,
	ConnectionList,
	IConnectionItem,
	ControllerConnectionItem,
	ConnectionType,
	ActionBehavior,
	ObsoleteBehaviorsList,
	BaseDataItemWithMetadata,
	ViewMode,
	BaseDataItem,
	DesignDataItemWithBackground,
	MediaRefDataItem,
	RepeaterDataItem,
} from '@wix/thunderbolt-becky-types'
import { InflatedByRefComp, InflatedByRefStructure, RefData, ResolvedRef } from '../types/byRef'

const DATA_MAPS = pointers.data.DATA_MAPS
const PROPERTY_TYPES = pointers.components.PROPERTY_TYPES
const QUERY_TO_MAP_NAME = pointers.QUERY_TO_MAP_NAME
const REF_DELIMITER = '_r_'
const REPEATER_ITEM_DELIMETER = '__'
const isMobile = (viewMode: ViewMode) => viewMode === 'mobile'

const DATA_QUERIES_TO_INFLATE = [
	PROPERTY_TYPES.BEHAVIOR_QUERY,
	PROPERTY_TYPES.BREAKPOINTS_QUERY,
	PROPERTY_TYPES.PROPERTY_QUERY,
	PROPERTY_TYPES.CONNECTION_QUERY,
	PROPERTY_TYPES.DESIGN_QUERY,
	PROPERTY_TYPES.DATA_QUERY,
	PROPERTY_TYPES.LAYOUT_QUERY,
	PROPERTY_TYPES.MOBILE_HINTS_QUERY,
	PROPERTY_TYPES.STYLE_ID,
	PROPERTY_TYPES.TRANSFORMATION_QUERY,
	PROPERTY_TYPES.TRANSITION_QUERY,
]

const resolveInternalRefs = (rootCompId: string, masterPageStructure: Structure, masterPageData: Data): ResolvedRef => {
	const structureResult: InflatedByRefStructure = {}

	// TODO: fix definition for data
	const dataResult: RefData = {
		[DATA_MAPS.BEHAVIORS]: {},
		[DATA_MAPS.BREAKPOINTS]: {},
		[DATA_MAPS.PROPERTIES]: {},
		[DATA_MAPS.CONNECTIONS]: {},
		[DATA_MAPS.DESIGN]: {},
		[DATA_MAPS.DATA]: {},
		[DATA_MAPS.LAYOUT]: {},
		[DATA_MAPS.MOBILE_HINTS]: {},
		[DATA_MAPS.STYLE]: {},
		[DATA_MAPS.TRANSFORMATIONS]: {},
		[DATA_MAPS.TRANSITIONS]: {},
		[DATA_MAPS.ANCHOR]: {},
		[DATA_MAPS.VARIANTS]: {},
	}

	const resolveAndAddComponentFromStructure = (component: Component) =>
		(structureResult[component.id] = { ...component, templateId: component.id })

	const resolveAndAddComponentDataItems = (component: Component) => {
		Object.keys(QUERY_TO_MAP_NAME).forEach((dataQuery) => {
			const compQuery = component[dataQuery as QueryProperty]
			if (!compQuery) {
				return
			}

			const dataMap = QUERY_TO_MAP_NAME[dataQuery as QueryProperty]
			dataResult[dataMap][compQuery] = {
				...masterPageData[dataMap][compQuery],
			}
		})
	}

	const resolveStructureAndDataRecursively = (component: Component) => {
		const childComponent = resolveAndAddComponentFromStructure(component)
		resolveAndAddComponentDataItems(childComponent)

		if (!component.components || component.components.length === 0) {
			return
		}
		component.components.forEach((childComponentId: string) => {
			const childComp = masterPageStructure[childComponentId]
			resolveStructureAndDataRecursively(childComp)
		})
	}

	const rootComp = masterPageStructure[rootCompId]
	resolveStructureAndDataRecursively(rootComp)

	return { structure: structureResult, data: dataResult, rootId: rootCompId }
}

const getUniqueId = (refId: string, originalId: string) => `${refId}${REF_DELIMITER}${originalId}`

const getUniqueDataQuery = (id: string, suffix: string) => `${id}-${suffix}`

const getRepeaterAncestorIfExists = (id: string | null, structure: Structure): string | null => {
	if (!id || !structure[id]) {
		return null
	}

	if (structure[id].componentType === 'Repeater') {
		return id
	}

	return getRepeaterAncestorIfExists(structure[id].parent, structure)
}

const fixInflatedConnectionItem = (
	connectionItem: ControllerConnectionItem,
	originalStructure: Structure,
	refId: string
) => {
	if (connectionItem.type !== ConnectionType.ConnectionItem || !connectionItem.controllerId) {
		return connectionItem
	}

	const originalComp = Object.values(originalStructure).find(
		(value) => value.dataQuery === connectionItem.controllerId
	)

	if (!originalComp) {
		return connectionItem
	}

	const oldCompUniqueId = getUniqueId(refId, originalComp.id)
	const inflatedId = getUniqueDataQuery(oldCompUniqueId, PROPERTY_TYPES.DATA_QUERY)

	return { ...connectionItem, controllerId: inflatedId }
}

const inflateDataQueries = (compStructure: Component, compUniqueId: string) => {
	return _(DATA_QUERIES_TO_INFLATE)
		.mapKeys((x) => x)
		.mapValues((dataQueryName) =>
			compStructure[dataQueryName] ? getUniqueDataQuery(compUniqueId, dataQueryName) : null
		)
		.pickBy((x) => x)
		.value()
}

const getEmptyDataMaps: () => RefData = () => ({
	[DATA_MAPS.BEHAVIORS]: {},
	[DATA_MAPS.BREAKPOINTS]: {},
	[DATA_MAPS.PROPERTIES]: {},
	[DATA_MAPS.CONNECTIONS]: {},
	[DATA_MAPS.DESIGN]: {},
	[DATA_MAPS.DATA]: {},
	[DATA_MAPS.LAYOUT]: {},
	[DATA_MAPS.MOBILE_HINTS]: {},
	[DATA_MAPS.STYLE]: {},
	[DATA_MAPS.TRANSFORMATIONS]: {},
	[DATA_MAPS.TRANSITIONS]: {},
	[DATA_MAPS.ANCHOR]: {},
	[DATA_MAPS.VARIANTS]: {},
})

const inflateSingleComp = (
	compStructure: Component,
	originalCompId: string,
	refId: string,
	rootId: string,
	pageData: Data,
	viewMode: ViewMode,
	resolvedData: Data | RefData
) => {
	const compUniqueId = getUniqueId(refId, originalCompId)
	const isRootComp = originalCompId === rootId

	const inflatedPropQuery = getUniqueDataQuery(
		compUniqueId,
		isMobile(viewMode) ? `${PROPERTY_TYPES.PROPERTY_QUERY}-mobile` : PROPERTY_TYPES.PROPERTY_QUERY
	)
	const originalPropQuery = compStructure[PROPERTY_TYPES.PROPERTY_QUERY]
		? compStructure[PROPERTY_TYPES.PROPERTY_QUERY]
		: ''

	const propertyItemOverrides: Partial<{ ghost?: string } & BaseDataItem<string>> = pageData[DATA_MAPS.PROPERTIES][
		inflatedPropQuery
	]
		? pageData[DATA_MAPS.PROPERTIES][inflatedPropQuery]
		: {}

	let originalPropertyItem: Partial<{ ghost?: string } & BaseDataItem<string>> = {}
	if (originalPropQuery) {
		originalPropertyItem = resolvedData[DATA_MAPS.PROPERTIES][originalPropQuery]
			? resolvedData[DATA_MAPS.PROPERTIES][originalPropQuery]
			: {}
	}

	const propertyItemToUse = propertyItemOverrides ? propertyItemOverrides : originalPropertyItem

	const isGhostCollapsed = propertyItemToUse.ghost === 'COLLAPSED'

	const ghostRefComp = {
		components: [],
		componentType: 'GhostRefComp',
		connectionQuery: null,
	}

	const inflatedSingleComp = {
		...compStructure,
		...inflateDataQueries(compStructure, compUniqueId),
		templateId: originalCompId,
		id: compUniqueId,
		parent: isRootComp ? refId : getUniqueId(refId, compStructure.parent as string),
		components: compStructure.components
			? compStructure.components.map((childCompId) => getUniqueId(refId, childCompId))
			: undefined,
	}

	if (isGhostCollapsed) {
		return {
			...inflatedSingleComp,
			...ghostRefComp,
		}
	} else {
		return inflatedSingleComp
	}
}

const inflateSingleDataItem = (
	dataMapName: DataMapName,
	dataQueryName: QueryProperty,
	comp: InflatedByRefComp,
	resolvedStructure: Structure,
	resolvedData: Data | RefData,
	refId: string
) => {
	const inflatedDataQuery = comp[dataQueryName]
	if (!inflatedDataQuery) {
		return
	}
	const originalCompId = comp.templateId
	const originalDataQuery = resolvedStructure[originalCompId][dataQueryName] as string
	const originalDataItem = resolvedData[dataMapName][originalDataQuery]
	const inflatedDataItem = { ...originalDataItem, id: inflatedDataQuery }
	if (dataQueryName === 'connectionQuery' && inflatedDataItem.type === 'ConnectionList') {
		// @ts-ignore
		inflatedDataItem.items = inflatedDataItem.items.map((connectionItem) =>
			fixInflatedConnectionItem(connectionItem, resolvedStructure, refId)
		)
	}

	return inflatedDataItem
}

const fixBackgroudDesignData = (
	masterPageDesignData: Data['design_data'] | RefData['design_data'],
	remoteDesignData: Data['design_data'] | RefData['design_data'],
	inflatedDataItem: DesignDataItemWithBackground
): Data['design_data'] | RefData['design_data'] => {
	const result: Data['design_data'] | RefData['design_data'] = {}

	if (inflatedDataItem.background) {
		const backgroundDataItem = (masterPageDesignData[inflatedDataItem.background] ||
			remoteDesignData[inflatedDataItem.background]) as MediaRefDataItem

		result[backgroundDataItem.id] = {
			...backgroundDataItem,
		}

		if (backgroundDataItem.mediaRef) {
			const mediaRefDataItem =
				masterPageDesignData[backgroundDataItem.mediaRef] || remoteDesignData[backgroundDataItem.mediaRef]

			result[mediaRefDataItem.id] = {
				...mediaRefDataItem,
			}
		}
	}
	return result
}

const inflateRepeaterItems = (
	repeaterAncestorId: string,
	dataMapName: 'design_data' | 'document_data',
	dataQueryName: 'designQuery' | 'dataQuery',
	comp: InflatedByRefComp,
	originalInflatedDataItemId: string,
	remoteAndMasterPageStructure: Structure | InflatedByRefStructure,
	masterPageData: Data | RefData,
	resolvedData: Data | RefData,
	pageCompId: string
): { [id: string]: BaseDataItemWithMetadata } => {
	const { dataQuery: repeaterDataQuery } = remoteAndMasterPageStructure[repeaterAncestorId]

	const repeaterDataItem =
		repeaterDataQuery &&
		((masterPageData[DATA_MAPS.DATA][repeaterDataQuery] ||
			resolvedData[DATA_MAPS.DATA][repeaterDataQuery]) as RepeaterDataItem)

	if (!repeaterDataItem) {
		return {}
	}

	const { items } = repeaterDataItem

	return items.reduce((acc: { [id: string]: BaseDataItemWithMetadata }, item: string) => {
		const originalDataItemId = `${
			remoteAndMasterPageStructure[comp.templateId][dataQueryName]
		}${REPEATER_ITEM_DELIMETER}${item}`
		const originalItemData = (masterPageData[dataMapName][originalDataItemId] ||
			resolvedData[dataMapName][originalDataItemId]) as BaseDataItemWithMetadata

		if (originalItemData) {
			const inflatedDataItemId = `${originalInflatedDataItemId}${REPEATER_ITEM_DELIMETER}${item}`
			acc[inflatedDataItemId] = {
				...originalItemData,
				id: inflatedDataItemId,
				metaData: { ...originalItemData.metaData, pageId: pageCompId },
			} as BaseDataItemWithMetadata
		}
		return acc
	}, {})
}

const inflateRef = (
	resolvedStructureAndData: ResolvedRef,
	refId: string,
	pageStructure: Structure,
	pageData: Data,
	masterPageStructure: Structure,
	masterPageData: Data,
	pageCompId: string,
	viewMode: ViewMode
): ResolvedRef => {
	const { rootId, structure: resolvedStructure, data: resolvedData } = resolvedStructureAndData

	const getRemoteAndMasterPageStructure = _.once(() => ({ ...masterPageStructure, ...resolvedStructure }))

	const inflatedStructure: InflatedByRefStructure = _(resolvedStructure)
		.mapValues((comp, originalCompId) =>
			inflateSingleComp(comp, originalCompId, refId, rootId, pageData, viewMode, resolvedData)
		)
		.mapKeys('id')
		.value()

	const inflatedData: RefData = _.reduce(
		inflatedStructure,
		(inflatedDataAcc, comp) => {
			DATA_QUERIES_TO_INFLATE.forEach((dataQueryName) => {
				const dataMapName = QUERY_TO_MAP_NAME[dataQueryName]
				const inflatedDataItem = inflateSingleDataItem(
					dataMapName,
					dataQueryName,
					comp,
					resolvedStructure,
					resolvedData,
					refId
				)
				if (inflatedDataItem) {
					inflatedDataAcc[dataMapName][inflatedDataItem.id] = inflatedDataItem

					if (dataQueryName === 'designQuery' && inflatedDataItem.type === 'MediaContainerDesignData') {
						_.assign(
							inflatedDataAcc[DATA_MAPS.DESIGN],
							fixBackgroudDesignData(
								masterPageData[DATA_MAPS.DESIGN],
								resolvedData[DATA_MAPS.DESIGN],
								inflatedDataItem as DesignDataItemWithBackground
							)
						)
					}

					if (dataQueryName === 'dataQuery' || dataQueryName === 'designQuery') {
						const remoteAndMasterPageStructure = getRemoteAndMasterPageStructure()

						const repeaterAncestorId = getRepeaterAncestorIfExists(
							comp.templateId,
							remoteAndMasterPageStructure
						)

						if (!repeaterAncestorId) {
							return
						}

						_.assign(
							inflatedDataAcc[dataMapName],
							inflateRepeaterItems(
								repeaterAncestorId,
								dataMapName as 'design_data' | 'document_data',
								dataQueryName,
								comp,
								inflatedDataItem.id,
								remoteAndMasterPageStructure,
								masterPageData,
								resolvedData,
								pageCompId
							)
						)
					}
				}
			})

			return inflatedDataAcc
		},
		getEmptyDataMaps()
	)

	return { structure: inflatedStructure, data: inflatedData, rootId: getUniqueId(refId, rootId) }
}

const mergeData = (original: Data, resolvedRefMapToMerge: { [key: string]: ResolvedRef }) =>
	_.merge({}, ...Object.values(resolvedRefMapToMerge).map((resolvedRef) => resolvedRef?.data ?? {}), original)

const fixDataAfterInflate = (pageData: Data, originalCompIdToInflatedCompIdMap: { [key: string]: string }) => {
	const fixBehaviourItem = (behaviourItem: ActionBehavior) => {
		if (behaviourItem.action && originalCompIdToInflatedCompIdMap[behaviourItem.action.sourceId]) {
			behaviourItem.action.sourceId = originalCompIdToInflatedCompIdMap[behaviourItem.action.sourceId]
		}

		if (behaviourItem.behavior && originalCompIdToInflatedCompIdMap[behaviourItem.behavior.targetId]) {
			behaviourItem.behavior.targetId = originalCompIdToInflatedCompIdMap[behaviourItem.behavior.targetId]
		}

		return behaviourItem
	}

	const fixConnectionItem = (connectionItem: IConnectionItem) => {
		if (
			connectionItem.type === ConnectionType.ConnectionItem ||
			!(connectionItem as ControllerConnectionItem).controllerId
		) {
			return connectionItem
		}

		return connectionItem
	}

	// Fix behaviors_data
	Object.keys(pageData.behaviors_data).forEach((behaviourId) => {
		const dataItem = pageData.behaviors_data[behaviourId] as ObsoleteBehaviorsList

		if (dataItem.type === 'ObsoleteBehaviorsList') {
			const itemsString = (dataItem as ObsoleteBehaviorsList).items
			try {
				const itemsList = JSON.parse(itemsString) as Array<ActionBehavior>
				dataItem.items = JSON.stringify(itemsList.map(fixBehaviourItem))
				pageData.behaviors_data[behaviourId] = dataItem
			} catch (e) {
				console.error('Cannot stringify ObsoleteBehaviorsList', e)
			}
		}
	})

	Object.keys(pageData.connections_data).forEach((behaviourId) => {
		const dataItem = pageData.connections_data[behaviourId] as ConnectionList

		if (dataItem.type === 'ConnectionList') {
			dataItem.items = dataItem.items.map(fixConnectionItem)
		}
	})

	return pageData
}
export {
	resolveInternalRefs,
	inflateRef,
	getUniqueId,
	getUniqueDataQuery,
	mergeData,
	fixDataAfterInflate,
	REF_DELIMITER,
	buildWidgetUrl,
}
